import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo, useEffect, useCallback } from "react";
import { 
  Plus, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar,
  Wallet,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  RefreshCw,
  Edit,
  Power,
  PowerOff,
  Home,
  Utensils,
  Car,
  HeartPulse,
  Dumbbell,
  Tv,
  Palmtree,
  Plane,
  Coins,
  FileText,
  Briefcase,
  Layers,
  Users2,
  Settings,
  Trash2,
  Lock
} from "lucide-react";
import { format, startOfMonth, endOfMonth, parseISO, isSameMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useMonthSelector } from "@/hooks/useMonthSelector";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { FinanceiroForm, FORMAS_PAGAMENTO, CATEGORIAS_BASE } from "@/components/financeiro/FinanceiroForm";
import { ContaRecorrenteForm } from "@/components/financeiro/ContaRecorrenteForm";
import { supabase } from "@/lib/supabase";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useUserRole } from "@/hooks/useUserRole";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

export const Route = createFileRoute("/financeiro")({
  component: FinanceiroPage,
});


function FinanceiroPage() {
  const [activeTab, setActiveTab] = useState("studio");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRecorrenteDialogOpen, setIsRecorrenteDialogOpen] = useState(false);
  const [editingRecorrente, setEditingRecorrente] = useState<any>(null);
  const [fixedTipo, setFixedTipo] = useState<"entrada" | "saida" | null>(null);
  const [financeiro, setFinanceiro] = useState<any[]>([]);
  const [contasRecorrentes, setContasRecorrentes] = useState<any[]>([]);
  const [agendamentosFuturos, setAgendamentosFuturos] = useState<any[]>([]);
  const [agendamentosMes, setAgendamentosMes] = useState<any[]>([]);
  const [equipe, setEquipe] = useState<any[]>([]);
  const [equipeAgg, setEquipeAgg] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [unificarFinancas, setUnificarFinancas] = useState(false);
  const [comissoesHabilitadas, setComissoesHabilitadas] = useState(true);
  const [categoriasCustom, setCategoriasCustom] = useState<{ saida_studio: any[]; despesa_pessoal: any[] }>({ saida_studio: [], despesa_pessoal: [] });
  const [novaCategoriaStudio, setNovaCategoriaStudio] = useState("");
  const [novaCategoriaPessoal, setNovaCategoriaPessoal] = useState("");
  const { selectedDate, MonthSelector } = useMonthSelector();
  const { isTatuador, comissaoPercentual } = useUserRole();

  const fetchFinanceiro = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const start = format(startOfMonth(selectedDate), "yyyy-MM-01");
      const end = format(endOfMonth(selectedDate), "yyyy-MM-dd");

      console.log("Buscando lançamentos para:", { studio_id: user.id, start, end });

      const { data, error } = await supabase
        .from("financeiro")
        .select("*")
        .eq("studio_id", user.id)
        .gte("data", start)
        .lte("data", end)
        .order("data", { ascending: false });

      if (error) throw error;
      
      console.log("Lançamentos retornados:", data);
      setFinanceiro(data || []);
    } catch (error: any) {
      console.error("Erro ao buscar dados financeiros:", error);
      toast.error("Erro ao carregar dados financeiros");
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate]);

  const fetchPreferencias = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("configuracoes")
        .select("unificar_financas, comissoes_habilitadas")
        .eq("studio_id", user.id)
        .single();
      if (data) {
        setUnificarFinancas(data.unificar_financas ?? false);
        setComissoesHabilitadas(data.comissoes_habilitadas ?? true);
      }
    } catch (error) {
      console.error("Erro ao buscar preferências:", error);
    }
  }, []);

  const fetchCategoriasCustom = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from("financeiro_categorias")
        .select("id, tipo, nome")
        .eq("studio_id", user.id)
        .order("nome");
      if (error) throw error;
      setCategoriasCustom({
        saida_studio: (data || []).filter(c => c.tipo === "saida_studio"),
        despesa_pessoal: (data || []).filter(c => c.tipo === "despesa_pessoal"),
      });
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
    }
  }, []);

  const handleAddCategoria = async (tipo: "saida_studio" | "despesa_pessoal", nomeInput: string) => {
    const nome = nomeInput.trim();
    if (!nome) return;

    const baseList = tipo === "saida_studio" ? CATEGORIAS_BASE.studio.saida : CATEGORIAS_BASE.pessoal.despesa;
    const customList = tipo === "saida_studio" ? categoriasCustom.saida_studio : categoriasCustom.despesa_pessoal;
    const jaExiste = [...baseList, ...customList.map(c => c.nome)]
      .some(existente => existente.toLowerCase() === nome.toLowerCase());
    if (jaExiste) {
      toast.error(`A categoria "${nome}" já existe`);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { error } = await supabase
        .from("financeiro_categorias")
        .insert({ studio_id: user.id, tipo, nome });
      if (error) throw error;
      if (tipo === "saida_studio") setNovaCategoriaStudio(""); else setNovaCategoriaPessoal("");
      fetchCategoriasCustom();
    } catch (error: any) {
      toast.error("Erro ao adicionar categoria: " + error.message);
    }
  };

  const handleDeleteCategoria = async (id: string) => {
    try {
      const { error } = await supabase.from("financeiro_categorias").delete().eq("id", id);
      if (error) throw error;
      fetchCategoriasCustom();
    } catch (error: any) {
      toast.error("Erro ao remover categoria: " + error.message);
    }
  };

  const handleToggleComissoes = async (val: boolean) => {
    setComissoesHabilitadas(val);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { error } = await supabase
        .from("configuracoes")
        .upsert({ studio_id: user.id, comissoes_habilitadas: val }, { onConflict: "studio_id" });
      if (error) throw error;
    } catch (error: any) {
      toast.error("Erro ao salvar preferência: " + error.message);
    }
  };

  const handleToggleUnificar = async (val: boolean) => {
    setUnificarFinancas(val);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { error } = await supabase
        .from("configuracoes")
        .upsert({ studio_id: user.id, unificar_financas: val }, { onConflict: "studio_id" });
      if (error) throw error;
    } catch (error: any) {
      toast.error("Erro ao salvar preferência: " + error.message);
    }
  };

  const fetchContasRecorrentes = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("contas_recorrentes")
        .select("*")
        .eq("studio_id", user.id)
        .order("nome");

      if (error) throw error;
      setContasRecorrentes(data || []);
    } catch (error) {
      console.error("Erro ao buscar contas recorrentes:", error);
    }
  }, []);

  const handleProcessRecurrentTasks = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const studio_id = user.id;
      const start = format(startOfMonth(selectedDate), "yyyy-MM-01");
      const end = format(endOfMonth(selectedDate), "yyyy-MM-dd");

      // Buscar contas recorrentes ativas
      const { data: activeContas } = await supabase
        .from("contas_recorrentes")
        .select("*")
        .eq("studio_id", studio_id)
        .eq("ativo", true);

      if (!activeContas || activeContas.length === 0) return;

      // Buscar lançamentos já gerados este mês (por conta de origem, não por texto)
      const { data: generatedThisMonth } = await supabase
        .from("financeiro")
        .select("conta_recorrente_id, data")
        .eq("studio_id", studio_id)
        .eq("geradoAutomaticamente", true)
        .not("conta_recorrente_id", "is", null)
        .gte("data", start)
        .lte("data", end);

      const generatedKeys = new Set(
        (generatedThisMonth || []).map(f => `${f.conta_recorrente_id}-${f.data}`)
      );

      let gerouAlgum = false;
      for (const conta of activeContas) {
        const diaStr = String(conta.dia_vencimento).padStart(2, '0');
        const dataVencimento = format(selectedDate, `yyyy-MM-${diaStr}`);
        const key = `${conta.id}-${dataVencimento}`;

        if (!generatedKeys.has(key)) {
          // Índice único (conta_recorrente_id, data) garante que, mesmo se isto
          // rodar em duplicidade (ex.: duas abas abertas ao mesmo tempo), só um
          // lançamento sobrevive — o segundo insert falha com 23505 e é ignorado.
          const { error: insertError } = await supabase.from("financeiro").insert({
            studio_id,
            conta_recorrente_id: conta.id,
            tipo: "saida",
            descricao: conta.nome,
            categoria: conta.categoria,
            valor: conta.valor,
            data: dataVencimento,
            status: "confirmado",
            geradoAutomaticamente: true
          });
          if (insertError && insertError.code !== "23505") {
            console.error("Erro ao gerar lançamento da conta recorrente:", insertError);
          } else if (!insertError) {
            gerouAlgum = true;
          }
        }
      }

      if (gerouAlgum) fetchFinanceiro();
    } catch (error) {
      console.error("Erro ao processar contas recorrentes:", error);
    }
  }, [selectedDate, fetchFinanceiro]);

  const fetchEquipe = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase
        .from("equipe")
        .select("id, tatuador_id, nome, comissao_percentual, status, usuarios:tatuador_id(nome)")
        .eq("studio_id", user.id)
        .eq("status", "ativo");
      if (error) throw error;
      setEquipe(data || []);
    } catch (error) {
      console.error("Erro ao buscar equipe:", error);
    }
  }, []);

  // DONO: total de agendamentos de cada tatuador da equipe no mês
  // (os agendamentos do tatuador ficam no studio_id dele; o dono os lê pela
  //  policy agendamentos_dono_equipe_select, filtrando por tatuador_id)
  const fetchComissoesEquipe = useCallback(async () => {
    if (isTatuador) {
      setEquipeAgg({});
      return;
    }
    const ids = equipe.map((m) => m.tatuador_id).filter(Boolean);
    if (ids.length === 0) {
      setEquipeAgg({});
      return;
    }
    const start = format(startOfMonth(selectedDate), "yyyy-MM-01");
    const end = format(endOfMonth(selectedDate), "yyyy-MM-dd");
    const { data } = await supabase
      .from("agendamentos")
      .select("tatuador_id, valor_total")
      .in("tatuador_id", ids)
      .eq("tipo", "cliente")
      .neq("status", "Cancelado")
      .gte("data", start)
      .lte("data", end);
    const agg: Record<string, number> = {};
    (data || []).forEach((a) => {
      agg[a.tatuador_id] = (agg[a.tatuador_id] || 0) + (parseFloat(a.valor_total) || 0);
    });
    setEquipeAgg(agg);
  }, [equipe, selectedDate, isTatuador]);

  useEffect(() => {
    fetchComissoesEquipe();
  }, [fetchComissoesEquipe]);

  const fetchAgendamentosFuturos = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const start = format(startOfMonth(selectedDate), "yyyy-MM-01");
      const end = format(endOfMonth(selectedDate), "yyyy-MM-dd");

      // Tatuador convidado enxerga por tatuador_id; dono enxerga por studio_id
      const scope = (q: any) =>
        isTatuador ? q.eq("tatuador_id", user.id) : q.eq("studio_id", user.id);

      // Pendentes: para exibir na tabela de pagamentos a receber
      const { data: pendentes, error } = await scope(
        supabase
          .from("agendamentos")
          .select("id, data, cliente_nome, valor_total, valor_sinal, status")
          .neq("status", "Concluído")
          .neq("status", "Cancelado")
          .eq("tipo", "cliente")
          .gte("data", start)
          .lte("data", end)
          .order("data", { ascending: true })
      );

      if (error) throw error;
      setAgendamentosFuturos(pendentes || []);

      // Todos do mês (exceto cancelados): faturamento + comissões
      const { data: todos } = await scope(
        supabase
          .from("agendamentos")
          .select("id, data, cliente_nome, valor_total, valor_sinal, status, tipo, tatuador_id")
          .neq("status", "Cancelado")
          .eq("tipo", "cliente")
          .gte("data", start)
          .lte("data", end)
      );

      setAgendamentosMes(todos || []);
    } catch (error) {
      console.error("Erro ao buscar agendamentos:", error);
    }
  }, [selectedDate, isTatuador]);

  useEffect(() => {
    fetchFinanceiro();
    fetchContasRecorrentes();
    fetchPreferencias();
    fetchCategoriasCustom();
    fetchEquipe();
    fetchAgendamentosFuturos();
    handleProcessRecurrentTasks();
  }, [fetchFinanceiro, fetchContasRecorrentes, fetchPreferencias, fetchCategoriasCustom, fetchEquipe, fetchAgendamentosFuturos, handleProcessRecurrentTasks]);

  // Ativa o Realtime para a tabela financeiro e contas_recorrentes
  useRealtimeSubscription("financeiro", fetchFinanceiro);
  useRealtimeSubscription("contas_recorrentes", fetchContasRecorrentes);
  useRealtimeSubscription("equipe", fetchEquipe);

  const stats = useMemo(() => {
    const studio = financeiro.filter(f => f.tipo !== "pessoal");
    // Faturamento vem dos agendamentos do mês (não dos lançamentos automáticos)
    // + entradas manuais adicionadas pelo usuário no financeiro
    const faturamentoAgendamentos = agendamentosMes.reduce((acc, a) => acc + (parseFloat(a.valor_total) || 0), 0);
    const entradasManuais = studio
      .filter(f => f.tipo === "entrada" && !f.geradoAutomaticamente)
      .reduce((acc, curr) => acc + curr.valor, 0);
    const faturamento = faturamentoAgendamentos + entradasManuais;
    const despesas = studio.filter(f => f.tipo === "saida").reduce((acc, curr) => acc + curr.valor, 0);
    // Comissão só existe para o tatuador convidado (percentual pago ao studio)
    const comissao = isTatuador ? (faturamentoAgendamentos * comissaoPercentual) / 100 : 0;
    const lucro = faturamento - despesas - comissao;

    const pessoal = financeiro.filter(f => f.tipo === "pessoal");
    const despesasPessoais = pessoal.reduce((acc, curr) => acc + (curr.tipo_lancamento !== "receita" ? curr.valor : 0), 0)
      + (unificarFinancas ? despesas : 0);
    const ganhosPessoais = pessoal.reduce((acc, curr) => acc + (curr.tipo_lancamento === "receita" ? curr.valor : 0), 0)
      + (unificarFinancas ? faturamento : 0);
    const lucroPessoal = ganhosPessoais - despesasPessoais;

    return { faturamento, despesas, comissao, lucro, despesasPessoais, ganhosPessoais, lucroPessoal };
  }, [financeiro, agendamentosMes, unificarFinancas, isTatuador, comissaoPercentual]);

  // DONO: comissão a receber de cada tatuador da equipe (baseado nos
  // agendamentos do tatuador no mês selecionado)
  const comissoesEquipe = useMemo(() => {
    return equipe
      .filter((m) => m.tatuador_id)
      .map((membro) => {
        const total = equipeAgg[membro.tatuador_id] || 0;
        const pct = Number(membro.comissao_percentual) || 0;
        return {
          id: membro.id,
          nome: (membro.usuarios as any)?.nome || membro.nome || "Sem nome",
          total,
          pct,
          comissao: (total * pct) / 100,
        };
      });
  }, [equipe, equipeAgg]);

  // TATUADOR: comissão que ele paga ao studio, total do mês e por tatuagem
  const minhaComissao = useMemo(() => {
    const pct = comissaoPercentual;
    const itens = agendamentosMes
      .map((a) => {
        const total = parseFloat(a.valor_total) || 0;
        return { id: a.id, cliente_nome: a.cliente_nome, data: a.data, total, comissao: (total * pct) / 100 };
      })
      .sort((a, b) => (a.data || "").localeCompare(b.data || ""));
    const totalMes = itens.reduce((acc, i) => acc + i.comissao, 0);
    return { pct, itens, totalMes };
  }, [agendamentosMes, comissaoPercentual]);

  const handleSave = async (data: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      const { recorrente: _recorrente, ...cleanData } = data;
      const insertData: any = {
        ...cleanData,
        studio_id: user.id,
        valor: parseFloat(data.valor),
        status: "confirmado",
        forma_pagamento: cleanData.forma_pagamento || null,
      };

      // Garante que lançamentos da aba pessoal nunca sejam salvos como tipo studio
      if (activeTab === "pessoal") {
        insertData.tipo = "pessoal";
        insertData.tipo_lancamento = fixedTipo === "entrada" ? "receita" : "despesa";
      }

      console.log("Salvando lançamento:", insertData);

      const { error } = await supabase
        .from("financeiro")
        .insert(insertData);

      if (error) throw error;

      toast.success("Lançamento salvo com sucesso!");
      setIsDialogOpen(false);
      fetchFinanceiro(); // Recarrega a lista explicitamente
    } catch (error: any) {
      console.error("Erro ao salvar lançamento financeiro:", error);
      toast.error("Erro ao salvar lançamento financeiro: " + error.message);
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  const formaPagamentoLabel = (val?: string) => FORMAS_PAGAMENTO.find(fp => fp.value === val)?.label;

  const MESES_ABREV = ["JAN","FEV","MAR","ABR","MAI","JUN","JUL","AGO","SET","OUT","NOV","DEZ"];
  const mesAbrev = (dateStr: string) => {
    const m = parseInt((dateStr || "").split("-")[1], 10);
    return MESES_ABREV[m - 1] || "";
  };

  const handleSaveRecorrente = async (data: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      if (editingRecorrente) {
        const { error } = await supabase
          .from("contas_recorrentes")
          .update(data)
          .eq("id", editingRecorrente.id);
        if (error) throw error;
        toast.success("Conta recorrente atualizada!");
      } else {
        const { error } = await supabase
          .from("contas_recorrentes")
          .insert({
            ...data,
            studio_id: user.id
          });
        if (error) throw error;
        toast.success("Conta recorrente salva!");
      }

      setIsRecorrenteDialogOpen(false);
      setEditingRecorrente(null);
      fetchContasRecorrentes(); // Recarrega explicitamente
    } catch (error: any) {
      console.error("Erro ao salvar conta recorrente:", error);
      toast.error("Erro ao salvar conta recorrente: " + error.message);
    }
  };

  const handleToggleRecorrente = async (conta: any) => {
    try {
      const { error } = await supabase
        .from("contas_recorrentes")
        .update({ ativo: !conta.ativo })
        .eq("id", conta.id);
      if (error) throw error;
      toast.success(conta.ativo ? "Conta desativada" : "Conta ativada");
      fetchContasRecorrentes(); // Recarrega explicitamente
    } catch (error: any) {
      toast.error("Erro ao alterar status");
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20 md:pb-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">Financeiro</h1>
            <p className="text-muted-foreground">Controle suas finanças do studio e pessoais</p>
          </div>
          <MonthSelector />
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="p-0 border-none bg-transparent max-w-lg shadow-none">
            <FinanceiroForm
              tipo_aba={activeTab as "studio" | "pessoal"}
              fixedTipo={fixedTipo || undefined}
              categoriasExtras={
                (activeTab === "studio" ? categoriasCustom.saida_studio : categoriasCustom.despesa_pessoal)
                  .map(c => c.nome)
              }
              title={activeTab === "pessoal" ? (fixedTipo === "entrada" ? "Nova Receita" : "Nova Despesa") : (fixedTipo === "entrada" ? "Nova Entrada" : (fixedTipo === "saida" ? "Nova Saída" : "Novo Lançamento"))}
              onSubmit={handleSave}
              onCancel={() => setIsDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>

        <Dialog open={isRecorrenteDialogOpen} onOpenChange={setIsRecorrenteDialogOpen}>
          <DialogContent className="p-0 border-none bg-transparent max-w-lg shadow-none">
            <ContaRecorrenteForm
              title={editingRecorrente ? "Editar Conta Recorrente" : "Nova Conta Recorrente"}
              initialData={editingRecorrente}
              onSubmit={handleSaveRecorrente}
              onCancel={() => setIsRecorrenteDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>

      </div>

      <Tabs defaultValue="studio" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-card border border-border h-11 p-1 mb-6">
          <TabsTrigger value="studio" className="data-[state=active]:bg-primary data-[state=active]:text-white font-semibold px-8">Studio</TabsTrigger>
          <TabsTrigger value="pessoal" className="data-[state=active]:bg-primary data-[state=active]:text-white font-semibold px-8">Pessoal</TabsTrigger>
          <TabsTrigger value="configuracoes" className="data-[state=active]:bg-primary data-[state=active]:text-white font-semibold px-6">
            <Settings className="mr-2 size-4" />
            Configurações
          </TabsTrigger>
        </TabsList>

        <TabsContent value="configuracoes" className="mt-0 space-y-6 max-w-2xl">
          <Card className="bg-card border-border overflow-hidden">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-start gap-3">
                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <Settings size={20} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground uppercase tracking-wide">Preferências Gerais</h3>
                  <p className="text-sm text-muted-foreground">Ajuste como o Financeiro se comporta para o seu studio.</p>
                </div>
              </div>

              <div className="border-t border-border" />

              {!isTatuador && (
                <div className="flex items-center justify-between p-4 rounded-xl bg-background border border-border">
                  <div className="space-y-0.5">
                    <p className="text-sm font-semibold text-foreground">Mostrar Comissões da Equipe</p>
                    <p className="text-xs text-muted-foreground">Desative se você trabalha sozinho(a) no studio</p>
                  </div>
                  <Switch checked={comissoesHabilitadas} onCheckedChange={handleToggleComissoes} />
                </div>
              )}

              <div className="flex items-center justify-between p-4 rounded-xl bg-background border border-border">
                <div className="space-y-0.5">
                  <p className="text-sm font-semibold text-foreground">Unificar finanças pessoais com o estúdio</p>
                  <p className="text-xs text-muted-foreground">Entradas e saídas do estúdio aparecem também nas suas finanças pessoais</p>
                </div>
                <Switch checked={unificarFinancas} onCheckedChange={handleToggleUnificar} />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border overflow-hidden">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-start gap-3">
                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <Layers size={20} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground uppercase tracking-wide">Categorias de Despesa — Studio</h3>
                  <p className="text-sm text-muted-foreground">Crie categorias próprias além das já existentes.</p>
                </div>
              </div>

              <div className="border-t border-border" />

              <div className="flex gap-2">
                <Input
                  value={novaCategoriaStudio}
                  onChange={(e) => setNovaCategoriaStudio(e.target.value)}
                  placeholder="Nova categoria..."
                  className="bg-background border-border h-11"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddCategoria("saida_studio", novaCategoriaStudio); } }}
                />
                <Button className="h-11 bg-primary hover:bg-primary/90 text-white font-semibold px-6" onClick={() => handleAddCategoria("saida_studio", novaCategoriaStudio)}>
                  <Plus className="mr-2 size-4" />
                  Adicionar
                </Button>
              </div>

              <div className="space-y-2">
                {CATEGORIAS_BASE.studio.saida.map(nome => (
                  <div key={nome} className="flex items-center justify-between p-4 rounded-xl bg-background border border-border">
                    <div className="flex items-center gap-3">
                      <div className="size-2 rounded-full bg-primary/60" />
                      <span className="font-semibold text-foreground">{nome}</span>
                    </div>
                    <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                      <Lock size={11} /> Padrão
                    </span>
                  </div>
                ))}
                {categoriasCustom.saida_studio.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-4 rounded-xl bg-background border border-border">
                    <div className="flex items-center gap-3">
                      <div className="size-2 rounded-full bg-primary/60" />
                      <span className="font-semibold text-foreground">{c.nome}</span>
                    </div>
                    <button onClick={() => handleDeleteCategoria(c.id)} className="text-muted-foreground hover:text-primary transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border overflow-hidden">
            <CardContent className="p-6 space-y-5">
              <div className="flex items-start gap-3">
                <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <Layers size={20} />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground uppercase tracking-wide">Categorias de Despesa — Pessoal</h3>
                  <p className="text-sm text-muted-foreground">Crie categorias próprias além das já existentes.</p>
                </div>
              </div>

              <div className="border-t border-border" />

              <div className="flex gap-2">
                <Input
                  value={novaCategoriaPessoal}
                  onChange={(e) => setNovaCategoriaPessoal(e.target.value)}
                  placeholder="Nova categoria..."
                  className="bg-background border-border h-11"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddCategoria("despesa_pessoal", novaCategoriaPessoal); } }}
                />
                <Button className="h-11 bg-primary hover:bg-primary/90 text-white font-semibold px-6" onClick={() => handleAddCategoria("despesa_pessoal", novaCategoriaPessoal)}>
                  <Plus className="mr-2 size-4" />
                  Adicionar
                </Button>
              </div>

              <div className="space-y-2">
                {CATEGORIAS_BASE.pessoal.despesa.map(nome => (
                  <div key={nome} className="flex items-center justify-between p-4 rounded-xl bg-background border border-border">
                    <div className="flex items-center gap-3">
                      <div className="size-2 rounded-full bg-primary/60" />
                      <span className="font-semibold text-foreground">{nome}</span>
                    </div>
                    <span className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                      <Lock size={11} /> Padrão
                    </span>
                  </div>
                ))}
                {categoriasCustom.despesa_pessoal.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-4 rounded-xl bg-background border border-border">
                    <div className="flex items-center gap-3">
                      <div className="size-2 rounded-full bg-primary/60" />
                      <span className="font-semibold text-foreground">{c.nome}</span>
                    </div>
                    <button onClick={() => handleDeleteCategoria(c.id)} className="text-muted-foreground hover:text-primary transition-colors">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="studio" className="mt-0 space-y-6">
          {/* COMISSÕES DA EQUIPE — apenas para o dono do studio, se habilitado */}
          {!isTatuador && comissoesHabilitadas && (
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <Users2 className="text-muted-foreground" size={20} />
                Comissões da Equipe
              </h3>
              <div className="bg-card border border-border rounded-xl overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-background">
                      <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Tatuador</th>
                      <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Total do mês</th>
                      <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest text-right">Comissão a receber</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#27272A]">
                    {comissoesEquipe.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="p-8 text-center text-muted-foreground text-sm italic">
                          Nenhum tatuador ativo na equipe
                        </td>
                      </tr>
                    ) : comissoesEquipe.map(item => (
                      <tr key={item.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="p-4 text-sm font-semibold text-foreground">{item.nome}</td>
                        <td className="p-4 text-sm text-[#10B981] font-semibold">{formatCurrency(item.total)}</td>
                        <td className="p-4 text-sm font-semibold text-primary text-right">
                          {formatCurrency(item.comissao)} <span className="text-muted-foreground font-normal">({item.pct}%)</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {comissoesEquipe.length > 0 && (
                    <tfoot>
                      <tr className="border-t border-border bg-background">
                        <td className="p-4 text-xs font-bold uppercase tracking-widest text-muted-foreground">Total</td>
                        <td className="p-4 text-sm font-bold text-[#10B981]">
                          {formatCurrency(comissoesEquipe.reduce((a, c) => a + c.total, 0))}
                        </td>
                        <td className="p-4 text-sm font-bold text-primary text-right">
                          {formatCurrency(comissoesEquipe.reduce((a, c) => a + c.comissao, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          )}

          {/* MINHA COMISSÃO — apenas para o tatuador convidado */}
          {isTatuador && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <PieChart className="text-muted-foreground" size={20} />
                  Comissão do Studio ({minhaComissao.pct}%)
                </h3>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium">Total a pagar no mês</p>
                  <p className="text-xl font-bold text-primary">{formatCurrency(minhaComissao.totalMes)}</p>
                </div>
              </div>
              <div className="bg-card border border-border rounded-xl overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-background">
                      <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Data</th>
                      <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Cliente</th>
                      <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Valor da tatuagem</th>
                      <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest text-right">Comissão desta tatuagem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#27272A]">
                    {minhaComissao.itens.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="p-8 text-center text-muted-foreground text-sm italic">
                          Nenhuma tatuagem neste mês
                        </td>
                      </tr>
                    ) : minhaComissao.itens.map(item => (
                      <tr key={item.id} className="hover:bg-white/[0.02] transition-colors">
                        <td className="p-4 text-sm text-muted-foreground">{item.data ? `${item.data.split('-')[2]}/${item.data.split('-')[1]}` : "—"}</td>
                        <td className="p-4 text-sm font-semibold text-foreground">{item.cliente_nome || "—"}</td>
                        <td className="p-4 text-sm text-foreground">{formatCurrency(item.total)}</td>
                        <td className="p-4 text-sm font-semibold text-primary text-right">{formatCurrency(item.comissao)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Cards Studio */}
          <div className={cn("grid grid-cols-1 gap-4", isTatuador ? "md:grid-cols-4" : "md:grid-cols-3")}>
            <Card className="bg-card border-border overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="size-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center text-[#10B981]">
                    <TrendingUp size={24} />
                  </div>
                </div>
                <p className="text-muted-foreground text-sm font-medium uppercase tracking-wider">Faturamento do Mês</p>
                <h3 className="text-2xl font-semibold text-[#10B981] mt-1">{formatCurrency(stats.faturamento)}</h3>
              </CardContent>
            </Card>

            <Card className="bg-card border-border overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <TrendingDown size={24} />
                  </div>
                </div>
                <p className="text-muted-foreground text-sm font-medium uppercase tracking-wider">Despesas do Mês</p>
                <h3 className="text-2xl font-semibold text-primary mt-1">{formatCurrency(stats.despesas)}</h3>
              </CardContent>
            </Card>

            {isTatuador && (
              <Card className="bg-card border-border overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="size-10 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-600">
                      <TrendingDown size={24} />
                    </div>
                    <Badge className="bg-amber-500/10 text-amber-600 border-none font-semibold">{comissaoPercentual}%</Badge>
                  </div>
                  <p className="text-muted-foreground text-sm font-medium uppercase tracking-wider">Comissão do Mês</p>
                  <h3 className="text-2xl font-semibold text-amber-600 mt-1">{formatCurrency(stats.comissao)}</h3>
                </CardContent>
              </Card>
            )}

            <Card className="bg-card border-border overflow-hidden border-b-4 border-b-white/10">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="size-10 rounded-lg bg-white/5 flex items-center justify-center text-foreground">
                    <DollarSign size={24} />
                  </div>
                </div>
                <p className="text-muted-foreground text-sm font-medium uppercase tracking-wider">Lucro Líquido</p>
                <h3 className={cn(
                  "text-2xl font-semibold mt-1",
                  stats.lucro >= 0 ? "text-foreground" : "text-primary"
                )}>
                  {formatCurrency(stats.lucro)}
                </h3>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ENTRADAS */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <ArrowUpRight className="text-[#10B981]" />
                  Entradas
                </h3>
                <Button 
                  onClick={() => {
                    setFixedTipo("entrada");
                    setIsDialogOpen(true);
                  }}
                  className="bg-[#10B981] hover:bg-[#10B981]/90 text-white font-semibold h-9"
                  size="sm"
                >
                  <Plus className="mr-2 size-4" />
                  Nova Entrada
                </Button>
              </div>
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="divide-y divide-[#27272A]">
                  {financeiro.filter(f => f.tipo === "entrada").length > 0 ? (
                    financeiro.filter(f => f.tipo === "entrada").map(f => (
                      <div key={f.id} className="p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="hidden md:flex flex-col items-center justify-center bg-background p-2 rounded-lg border border-border min-w-[60px]">
                            <span className="text-[10px] text-muted-foreground font-medium uppercase">{f.data.split('-')[2]}</span>
                            <span className="text-xs text-foreground font-medium uppercase">{mesAbrev(f.data)}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-foreground">{f.descricao}</p>
                              {f.geradoAutomaticamente && (
                                <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 text-[8px] font-medium uppercase h-4">
                                  Agendamento
                                </Badge>
                              )}
                            </div>
                            <p className="text-[10px] text-muted-foreground font-medium uppercase mt-0.5">{f.categoria}</p>
                          </div>
                        </div>
                        <span className={cn(
                          "text-sm font-semibold text-[#10B981]",
                          f.status === "cancelado" && "line-through opacity-50"
                        )}>
                          {formatCurrency(f.valor)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-muted-foreground text-sm italic">Nenhuma entrada registrada</div>
                  )}
                </div>
              </div>
            </div>

            {/* SAIDAS */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                  <ArrowDownRight className="text-primary" />
                  Saídas
                </h3>
                <Button 
                  onClick={() => {
                    setFixedTipo("saida");
                    setIsDialogOpen(true);
                  }}
                  className="bg-primary hover:bg-primary/90 text-white font-semibold h-9"
                  size="sm"
                >
                  <Plus className="mr-2 size-4" />
                  Nova Saída
                </Button>
              </div>
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="divide-y divide-[#27272A]">
                  {financeiro.filter(f => f.tipo === "saida").length > 0 ? (
                    financeiro.filter(f => f.tipo === "saida").map(f => (
                      <div key={f.id} className="p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="hidden md:flex flex-col items-center justify-center bg-background p-2 rounded-lg border border-border min-w-[60px]">
                            <span className="text-[10px] text-muted-foreground font-medium uppercase">{f.data.split('-')[2]}</span>
                            <span className="text-xs text-foreground font-medium uppercase">{mesAbrev(f.data)}</span>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground">{f.descricao}</p>
                            <p className="text-[10px] text-muted-foreground font-medium uppercase mt-0.5">
                              {f.categoria}
                              {formaPagamentoLabel(f.forma_pagamento) && ` · ${formaPagamentoLabel(f.forma_pagamento)}`}
                            </p>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-primary">{formatCurrency(f.valor)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-muted-foreground text-sm italic">Nenhuma saída registrada</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Entradas Futuras */}
          <div className="space-y-4 pt-4">
            <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Calendar className="text-muted-foreground" />
              Entradas Futuras
            </h3>
            <div className="bg-card border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border bg-background">
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Data</th>
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Cliente</th>
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Total</th>
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Sinal Pago</th>
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest text-right">Saldo Restante</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A]">
                  {agendamentosFuturos.length > 0 ? agendamentosFuturos.map(f => (
                    <tr key={f.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="p-4 text-sm font-semibold text-muted-foreground">{f.data.split('-')[2]}/{f.data.split('-')[1]}</td>
                      <td className="p-4 text-sm font-semibold text-foreground">{f.cliente_nome}</td>
                      <td className="p-4 text-sm font-semibold text-muted-foreground">{formatCurrency(f.valor_total)}</td>
                      <td className="p-4 text-sm font-semibold text-[#10B981]">{formatCurrency(f.valor_sinal ?? 0)}</td>
                      <td className="p-4 text-sm font-semibold text-primary text-right">
                        <span className="flex items-center justify-end gap-2">
                          {formatCurrency((f.valor_total ?? 0) - (f.valor_sinal ?? 0))}
                          <ChevronRight size={14} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground text-sm italic">
                        Nenhum agendamento pendente neste mês
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Contas Recorrentes */}
          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <RefreshCw className="text-muted-foreground" />
                Contas Recorrentes
              </h3>
              <Button 
                onClick={() => {
                  setEditingRecorrente(null);
                  setIsRecorrenteDialogOpen(true);
                }}
                className="bg-primary hover:bg-primary/90 text-white font-semibold h-9"
                size="sm"
              >
                <Plus className="mr-2 size-4" />
                Nova Conta Recorrente
              </Button>
            </div>
            <div className="bg-card border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-border bg-background">
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Nome</th>
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Categoria</th>
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Valor</th>
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Vencimento</th>
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Status</th>
                    <th className="p-4 text-[10px] font-medium text-muted-foreground uppercase tracking-widest text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272A]">
                  {contasRecorrentes.map(conta => (
                    <tr key={conta.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="p-4 text-sm font-semibold text-foreground">{conta.nome}</td>
                      <td className="p-4 text-sm text-muted-foreground uppercase text-[10px] font-medium tracking-wider">{conta.categoria}</td>
                      <td className="p-4 text-sm font-semibold text-primary">{formatCurrency(conta.valor)}</td>
                      <td className="p-4 text-sm font-semibold text-muted-foreground">Dia {conta.dia_vencimento}</td>
                      <td className="p-4">
                        <Badge className={cn(
                          "text-[10px] font-medium uppercase px-2 py-0.5",
                          conta.ativo ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-zinc-500/10 text-muted-foreground border-zinc-500/20"
                        )}>
                          {conta.ativo ? "Ativa" : "Inativa"}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="size-8 text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setEditingRecorrente(conta);
                              setIsRecorrenteDialogOpen(true);
                            }}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className={cn(
                              "size-8",
                              conta.ativo ? "text-primary hover:text-primary/80" : "text-emerald-500 hover:text-emerald-500/80"
                            )}
                            onClick={() => handleToggleRecorrente(conta)}
                          >
                            {conta.ativo ? <PowerOff size={14} /> : <Power size={14} />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {contasRecorrentes.length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-muted-foreground text-sm italic">
                        Nenhuma conta recorrente cadastrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Contas a Receber */}
          <div className="pt-4">
            <Card className="bg-card border-border border-l-4 border-l-[#10B981]">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-widest mb-1">Total a Receber</h4>
                    <p className="text-xs text-muted-foreground">Saldo acumulado previsto até o final do mês</p>
                  </div>
                  <h3 className="text-3xl font-semibold text-foreground">
                    {formatCurrency(agendamentosFuturos.reduce((acc, curr) => acc + ((curr.valor_total ?? 0) - (curr.valor_sinal ?? 0)), 0))}
                  </h3>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pessoal" className="mt-0 space-y-6">
          {/* RESUMO DO MÊS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-card border-border overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="size-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center text-[#10B981]">
                    <TrendingUp size={24} />
                  </div>
                </div>
                <p className="text-muted-foreground text-sm font-medium uppercase tracking-wider">Total de Receitas</p>
                <h3 className="text-2xl font-semibold text-[#10B981] mt-1">{formatCurrency(stats.ganhosPessoais)}</h3>
              </CardContent>
            </Card>

            <Card className="bg-card border-border overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <TrendingDown size={24} />
                  </div>
                </div>
                <p className="text-muted-foreground text-sm font-medium uppercase tracking-wider">Total de Despesas</p>
                <h3 className="text-2xl font-semibold text-primary mt-1">{formatCurrency(stats.despesasPessoais)}</h3>
              </CardContent>
            </Card>

            <Card className="bg-card border-border overflow-hidden">
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="size-10 rounded-lg bg-white/5 flex items-center justify-center text-foreground">
                    <DollarSign size={24} />
                  </div>
                </div>
                <p className="text-muted-foreground text-sm font-medium uppercase tracking-wider">Saldo do Mês</p>
                <h3 className={cn(
                  "text-2xl font-semibold mt-1",
                  stats.lucroPessoal >= 0 ? "text-[#10B981]" : "text-primary"
                )}>
                  {formatCurrency(stats.lucroPessoal)}
                </h3>
              </CardContent>
            </Card>
          </div>

          {/* LANÇAMENTOS E GRÁFICO */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-3">
                <Button 
                  onClick={() => {
                    setFixedTipo("entrada");
                    setIsDialogOpen(true);
                  }}
                  className="flex-1 bg-[#10B981] hover:bg-[#10B981]/90 text-white font-semibold h-11"
                >
                  <Plus className="mr-2 size-4" />
                  Nova Receita
                </Button>
                <Button 
                  onClick={() => {
                    setFixedTipo("saida");
                    setIsDialogOpen(true);
                  }}
                  className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold h-11"
                >
                  <Plus className="mr-2 size-4" />
                  Nova Despesa
                </Button>
              </div>

              <div className="space-y-6 pt-2">
                {useMemo(() => {
                  const pessoal = unificarFinancas
                    ? [
                        ...financeiro.filter(f => f.tipo === "pessoal"),
                        ...financeiro
                          .filter(f => f.tipo === "entrada" || f.tipo === "saida")
                          .map(f => ({ ...f, _fromStudio: true, tipo_lancamento: f.tipo === "entrada" ? "receita" : "despesa" }))
                      ].sort((a, b) => b.data.localeCompare(a.data))
                    : financeiro.filter(f => f.tipo === "pessoal");
                  if (pessoal.length === 0) return <div className="p-8 text-center text-muted-foreground text-sm italic">Nenhum lançamento registrado</div>;

                  // Agrupar por data
                  const grouped: Record<string, any[]> = {};
                  pessoal.forEach(f => {
                    const date = f.data;
                    if (!grouped[date]) grouped[date] = [];
                    grouped[date].push(f);
                  });

                  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

                  const getIcon = (category: string) => {
                    const icons: Record<string, any> = {
                      "Moradia": Home,
                      "Alimentação": Utensils,
                      "Transporte": Car,
                      "Saúde": HeartPulse,
                      "Academia": Dumbbell,
                      "Assinaturas": Tv,
                      "Lazer": Palmtree,
                      "Viagem": Plane,
                      "Investimentos": Coins,
                      "Salário": Briefcase,
                      "Freelance": FileText,
                      "Outros": Layers
                    };
                    const IconComp = icons[category] || Layers;
                    return <IconComp size={18} />;
                  };

                  const formatDateLabel = (dateStr: string) => {
                    const d = parseISO(dateStr);
                    const today = new Date();
                    const yesterday = new Date();
                    yesterday.setDate(today.getDate() - 1);

                    if (format(d, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) return "Hoje";
                    if (format(d, 'yyyy-MM-dd') === format(yesterday, 'yyyy-MM-dd')) return "Ontem";
                    return format(d, "dd 'de' MMMM", { locale: ptBR });
                  };

                  return sortedDates.map(date => (
                    <div key={date} className="space-y-2">
                      <h4 className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider ml-1">{formatDateLabel(date)}</h4>
                      <div className="bg-card border border-border rounded-xl overflow-hidden divide-y divide-[#27272A]">
                        {grouped[date].map(f => (
                          <div key={f.id} className="p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors">
                            <div className="flex items-center gap-4">
                              <div className={cn(
                                "size-10 rounded-lg flex items-center justify-center",
                                f.tipo_lancamento === "receita" ? "bg-[#10B981]/10 text-[#10B981]" : "bg-primary/10 text-primary"
                              )}>
                                {getIcon(f.categoria)}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-foreground">{f.descricao}</p>
                                  {f._fromStudio && (
                                    <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 text-[8px] font-medium uppercase h-4">
                                      Studio
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-[10px] text-muted-foreground font-medium uppercase mt-0.5">
                                  {f.categoria}
                                  {formaPagamentoLabel(f.forma_pagamento) && ` · ${formaPagamentoLabel(f.forma_pagamento)}`}
                                </p>
                              </div>
                            </div>
                            <span className={cn(
                              "text-sm font-bold",
                              f.tipo_lancamento === "receita" ? "text-[#10B981]" : "text-primary"
                            )}>
                              {f.tipo_lancamento === "receita" ? "+" : "-"}{formatCurrency(f.valor)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ));
                }, [financeiro, unificarFinancas])}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <PieChart className="text-muted-foreground" />
                Despesas por Categoria
              </h3>
              <Card className="bg-card border-border p-6">
                {useMemo(() => {
                  const despesas = [
                    ...financeiro.filter(f => f.tipo === "pessoal" && f.tipo_lancamento !== "receita"),
                    ...(unificarFinancas ? financeiro.filter(f => f.tipo === "saida") : [])
                  ];
                  if (despesas.length === 0) return <div className="text-center text-muted-foreground text-sm italic">Nenhuma despesa para exibir</div>;

                  const total = despesas.reduce((acc, curr) => acc + curr.valor, 0);
                  const byCat: Record<string, number> = {};
                  despesas.forEach(d => {
                    byCat[d.categoria] = (byCat[d.categoria] || 0) + d.valor;
                  });

                  const colors: Record<string, string> = {
                    "Moradia": "#DC2626",
                    "Alimentação": "#10B981",
                    "Transporte": "#3B82F6",
                    "Saúde": "#EC4899",
                    "Academia": "#8B5CF6",
                    "Assinaturas": "#F59E0B",
                    "Lazer": "#06B6D4",
                    "Viagem": "#6366F1",
                    "Investimentos": "#14B8A6",
                    "Outros": "#71717A"
                  };

                  return (
                    <div className="space-y-6">
                      {Object.entries(byCat).sort((a, b) => b[1] - a[1]).map(([cat, val]) => {
                        const percent = Math.round((val / total) * 100);
                        return (
                          <div key={cat} className="space-y-2">
                            <div className="flex justify-between text-xs font-medium uppercase tracking-wider">
                              <span className="text-muted-foreground">{cat}</span>
                              <span className="text-foreground">{percent}%</span>
                            </div>
                            <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full" 
                                style={{ width: `${percent}%`, backgroundColor: colors[cat] || "#71717A" }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }, [financeiro, unificarFinancas])}
              </Card>

              {/* CONTAS RECORRENTES PESSOAIS */}
              <div className="pt-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <RefreshCw className="text-muted-foreground size-5" />
                    Recorrentes
                  </h3>
                  <Button 
                    onClick={() => {
                      setEditingRecorrente(null);
                      setIsRecorrenteDialogOpen(true);
                    }}
                    variant="outline"
                    className="border-primary/50 text-primary hover:bg-primary/10 h-8 text-xs"
                    size="sm"
                  >
                    <Plus className="mr-1 size-3" />
                    Adicionar
                  </Button>
                </div>
                <div className="bg-card border border-border rounded-xl divide-y divide-[#27272A]">
                  {contasRecorrentes.length > 0 ? (
                    contasRecorrentes.map(conta => (
                      <div key={conta.id} className="p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-foreground">{conta.nome}</p>
                          <p className="text-[10px] text-muted-foreground uppercase">Dia {conta.dia_vencimento}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-primary">{formatCurrency(conta.valor)}</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="size-7 text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setEditingRecorrente(conta);
                              setIsRecorrenteDialogOpen(true);
                            }}
                          >
                            <Edit size={12} />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-center text-[10px] text-muted-foreground italic">Nenhuma conta recorrente</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
